import React, { useState } from 'react';
import { useTranslation } from 'react-i18next/';

import PreprUtil from '@utils/PreprUtil';
import {
  ADVERTISEMENT_DEVICE,
  ADVERTISEMENT_TYPE,
  IMAGE_SIZES,
  NavbarTabsTypes,
} from '@constants/enums';

import styles from './Single.module.scss';

import { Meta } from '@components/layout/Meta/Meta';
import LoginModal from '@components/modals/LoginModal/LoginModal';
import RegisterModal from '@components/modals/RegisterModal/RegisterModal';
import PrimaryHeader from '@components/ui/PrimaryHeader/PrimaryHeader';
import TipListItem from '@components/ui/TipListItem/TipListItem';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';
import { FB_APP_ID, REVALIDATION_TIME } from '@constants/config';
import { DestinationLinksProvider } from '@hooks/useDestinationLinks';
import { FooterLinksProvider } from '@hooks/useFooterLinks';
import { SiteLinksProvider } from '@hooks/useSiteLinks';
import GenericService from '@services/GenericService';
import GraphqlService from '@services/GraphqlService';
import MailchimpService from '@services/MailchimpService';
import PersonalService from '@services/PersonalService';
import { GetStaticPropsContext } from 'next';
import { IManualTopListPublicationFields, TopListModule } from 'modules/TopListModule';
import { PageProps } from '@constants/types';
import { useAdvertisement } from '@hooks/useAdvertisement';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<IManualTopListPublicationFields> {}

const ManualTopList = ({ fields, footerLinks, siteLinks, destinationLinks }: Props) => {
  const { t } = useTranslation();
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);

  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.toplistContainer a`,
    4,
  );
  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.toplistContainer a`,
    11,
  );
  useAdvertisement(
    ADVERTISEMENT_TYPE.ARTICLE,
    ADVERTISEMENT_DEVICE.DESKTOP_AND_MOBILE,
    `.toplistContainer a`,
    16,
  );

  const handleRegisterClick = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(true);
  };

  const renderTipList = () => {
    return fields.items?.map((item) => (
      <TipListItem
        key={item._id}
        className="mb-4"
        tip={PreprUtil.formatToListItemProps(item, undefined, undefined, IMAGE_SIZES.FULL)}
        onUnauthorizedAdd={() => setLoginModalOpen(true)}
      />
    ));
  };

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout selectedTab={NavbarTabsTypes.TOPLISTS} paddings={false}>
              <Meta
                title={fields.meta_title || fields.title}
                description={fields.meta_description || fields.subtitle}
                image={fields.thumbnail?.[0]?.cdn_files[0]?.full}
              />
              <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                onRegisterClick={handleRegisterClick}
              />
              <RegisterModal
                isOpen={isRegisterModalOpen}
                onClose={() => setRegisterModalOpen(false)}
              />
              <div className={styles.ManualTopList}>
                <PrimaryHeader
                  backgroundImage={fields.thumbnail[0]?.cdn_files[0]?.full}
                  title={fields.title}
                  description={fields.subtitle}
                />
                <div className="container">
                  <h2 className={styles.Header}>
                    {t('DOMAIN.BUCKETLIST.TOP_X_LIST', { total: fields.items.length })}
                  </h2>
                  <div className="toplistContainer">{renderTipList()}</div>
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default ManualTopList;

export const getStaticProps = async ({ locale, preview, params }: GetStaticPropsContext) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await TopListModule.getSinglePage(String(params.toplist));

  if (!page) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: REVALIDATION_TIME,
  };
};

export const getStaticPaths = async () => {
  const paths = {
    paths: [],
    fallback: 'blocking',
  };

  return paths;
};
